import { Injector } from '@angular/core';
import { Router } from '@angular/router';
import { FormID } from '@finxone-platform/form-action';
import { AlertHandlerService } from '@finxone-platform/shared/services';
import {
  ActionButtonOutcomesType,
  AddCardRequest,
  BaseWidgetProperties,
  ConfirmationClaimType,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import { take } from 'rxjs';
import { CardManagementService } from '../../../services/card-management-service/card-management-service';
import { ConfirmationService } from '../../../services/confirmation-service/confirmation-service';
import { AccountState } from '../../../state/account.state';
import { FormActionState } from '../../../state/form-submision.state';
import { handleRedirection } from '../cta-redirection.utils';
import { getQueryParamValueByKey } from '../../query-params.utils';
import { ResetFormDataAction } from '../../../actions/form-submission.action';

/**
 * Collects the add-card form data along with the current user and account context,
 * then submits it to the card service. Adding a card always requires confirmation:
 * the user is routed through the confirmation flow and the card is only submitted
 * once confirmation completes. Navigates to the success or failure outcome based on
 * the API response.
 *
 * @param injector - The Angular Injector used to retrieve services.
 * @param widgetProperties - The widget properties used to resolve redirection targets.
 */
export const addCard = (injector: Injector, widgetProperties: BaseWidgetProperties) => {
  const store = injector.get(Store);
  const cardsService = injector.get(CardManagementService);
  const alertHandlerService = injector.get(AlertHandlerService);
  const formData = store.selectSnapshot(
    FormActionState.getFormActionStateWithId(FormID.ADD_CARD_FORM),
  )?.formData;
  const accountId = store.selectSnapshot(AccountState.getCurrentAccountId);

  if (!formData?.type || !formData?.cardReferenceType) {
    alertHandlerService.showAlertFn('error', 'Card details are missing.');
    return;
  }

  const payload: AddCardRequest = {
    ...formData,
    accountIds: [accountId],
  } as AddCardRequest;

  const submitCard = (accessToken?: string) =>
    cardsService
      .addCard(payload, accessToken)
      .pipe(take(1))
      .subscribe({
        next: () => {
          store.dispatch(new ResetFormDataAction());
          handleRedirection(widgetProperties, injector, ActionButtonOutcomesType.SUCCESS, true, false);
        },
        error: (error) => {
          store.dispatch(new ResetFormDataAction());
          console.error('Error adding card:', error);
          handleRedirection(widgetProperties, injector, ActionButtonOutcomesType.FAILURE, true, false);
        },
      });

  const router = injector.get(Router);
  const confirmationService = injector.get(ConfirmationService);
  // Strip query params so the returning URL still matches once confirmation completes.
  const returnUrl = router.url.split('?')[0];

  confirmationService
    .checkConfirmation(returnUrl)
    .pipe(take(1))
    .subscribe(() => {
      const accessToken = getQueryParamValueByKey('confirmation');
      submitCard(accessToken);
    });

  confirmationService.confirmAction(ConfirmationClaimType.ADD_CARD, returnUrl, true);
};
